import { Fragment, useEffect, useState, useCallback } from "react";
import { useApiClient } from "../api/useApiClient";
import type { ReviewItem, CuentaCatalogo, CompradorOption, LoteResultado } from "../types/reviewItem";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { NotasAdjuntoRow, NotasToggle } from "../components/NotasAdjuntoRow";
import { useAdjunto } from "../hooks/useAdjunto";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { CuentaCombobox, esCuentaValida } from "../components/CuentaCombobox";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { ModalMotivo } from "../components/ModalMotivo";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha } from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";
import { useAccion } from "../hooks/useAccion";
import { AvisoError } from "../components/AvisoError";
import { useContadores } from "../contexts/ContadoresContext";
import { ETIQUETA_AUTORIZAR_CONTADURIA, ETIQUETA_AUTORIZAR_CONTADURIA_LOTE } from "../constants/etiquetas";

const COLUMNAS = 8;

/**
 * Paso 1 — Contaduría. Filtra PENDIENTE_CONTADURIA por default (ver
 * GET /review-items?paso=contaduria&filtro=pendientes).
 *
 * Reglas de negocio que esta pantalla debe respetar (ver CLAUDE.md del
 * backend — no reinventar, ya está decidido):
 *  - Si `requiere_reparto_manual = true`: la cuenta se muestra de SOLO
 *    LECTURA, sin selector — nunca armar UI de reparto multi-línea acá,
 *    eso se resuelve en SAP.
 *  - Selección múltiple + autorización en lote.
 *  - Comprador sugerido puede venir vacío (sin default) — no asumir que
 *    siempre hay uno.
 *
 * [REESTRUCTURADA 08/08/2026] Pasó de 9 a 8 columnas mostrando MÁS datos:
 * Tipo de CFE, Número y RUT se plegaron en la celda "Documento"
 * (CfeIdentidad) y se quitó la columna Estado, que era redundante en una
 * bandeja filtrada a un único estado. Ver docs/Decisions.md.
 */
export function ContaduriaPage() {
  const api = useApiClient();
  const { refrescar: refrescarContadores } = useContadores();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [cuentas, setCuentas] = useState<CuentaCatalogo[]>([]);
  const [compradores, setCompradores] = useState<CompradorOption[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // [NUEVO 08/09/2026] Fila de Notas (solo el comprobante adjunto acá — el
  // comentario es del Paso 2). Independiente de `expandidos` (Líneas).
  const [notasAbiertas, setNotasAbiertas] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rechazando, setRechazando] = useState<ReviewItem | null>(null);

  // Ediciones locales antes de autorizar (cuenta/comprador/fecha por item)
  const [ediciones, setEdiciones] = useState<Record<string, { cuenta?: string; comprador?: string; fecha?: string }>>({});

  const tabla = useTablaBandeja(items, {
    camposBusqueda: ["proveedor_nombre", "rut_emisor", "nro", "serie"],
    ordenInicial: { columna: "fecha_documento", direccion: "desc" },
    camposNumericos: ["monto"],
    claveEstable: "id",
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reviewItems, catalogoCuentas, listaCompradores] = await Promise.all([
        api.listarReviewItems("contaduria", "pendientes"),
        api.listarCatalogoCuentas(),
        api.listarCompradores(),
      ]);
      setItems(reviewItems);
      setCuentas(catalogoCuentas);
      setCompradores(listaCompradores);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido al cargar la bandeja.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /** Tras una acción: recargar lista, limpiar selección y refrescar el badge
   *  del menú (si no, el menú seguiría mostrando el conteo viejo). */
  const recargarTodo = useCallback(async () => {
    setSelected(new Set());
    await cargar();
    refrescarContadores();
  }, [cargar, refrescarContadores]);

  const accion = useAccion(recargarTodo);
  const subirAdjunto = useAdjunto(accion);

  function toggleSelected(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /** Selecciona/deselecciona SOLO la página visible — no las 63 filas de la
   *  bandeja completa, que sería una acción sorpresivamente masiva. */
  function toggleSelectedPagina(checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const item of tabla.filas) {
        if (checked) next.add(item.id);
        else next.delete(item.id);
      }
      return next;
    });
  }

  function toggleNotas(id: string) {
    setNotasAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleExpandido(id: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** La cuenta efectiva de una fila: la editada localmente o la que vino. */
  function cuentaDe(item: ReviewItem): string {
    return ediciones[item.id]?.cuenta ?? item.cuenta_vigente;
  }

  /**
   * El Aprobador efectivo: el elegido en el desplegable o el que ya venía.
   *
   * [NUEVO 10/08/2026] Es OBLIGATORIO para autorizar, y no por prolijidad: la
   * bandeja del Aprobador ahora filtra por asignación, así que una factura
   * autorizada sin Aprobador no aparecería en la bandeja de NADIE. El backend
   * lo valida (400); acá se deshabilita el botón como control blando, mismo
   * patrón que la cuenta contable inválida.
   */
  function aprobadorDe(item: ReviewItem): string | undefined {
    return ediciones[item.id]?.comprador ?? item.comprador_asignado ?? undefined;
  }

  // Todas las acciones pasan por `accion.ejecutar`: refresca la lista SIEMPRE
  // (haya funcionado o no) y muestra el error sin destruir la tabla. Ver
  // useAccion.ts y el incidente del 10/08/2026 en docs/Decisions.md.
  async function autorizar(id: string) {
    const edicion = ediciones[id] ?? {};
    await accion.ejecutar(
      () =>
        api.autorizarContaduria(id, {
          cuenta_editada: edicion.cuenta,
          comprador_editado: edicion.comprador,
          fecha_documento: edicion.fecha,
        }),
      { descripcion: "autorizar la factura" }
    );
  }

  async function confirmarRechazo(motivo: string) {
    if (!rechazando) return;
    const id = rechazando.id;
    setRechazando(null);
    await accion.ejecutar(() => api.rechazar(id, { motivo, origen: "contaduria" }), {
      descripcion: "rechazar la factura",
    });
  }

  async function autorizarSeleccionadas() {
    if (selected.size === 0) return;
    // Se mandan las asignaciones del desplegable, no solo los ids: antes se
    // perdían en el lote, y con el Aprobador obligatorio eso habría hecho que
    // el backend rechazara todo.
    const porId = new Map(items.map((i) => [i.id, i]));
    const aEnviar = Array.from(selected).map((id) => {
      const item = porId.get(id);
      return { id, comprador_editado: item ? aprobadorDe(item) : undefined };
    });

    const resultado = await accion.ejecutar(
      () => api.autorizarContaduriaLote({ items: aEnviar }),
      { descripcion: `autorizar las ${selected.size} facturas seleccionadas` }
    );
    const parcial = resultado?.sin_aprobador?.length ? resultado : null;

    // Resultado PARCIAL: el POST salió bien pero algunas no se autorizaron. Se
    // avisa por el mismo cartel — no es un error de la llamada, pero tampoco
    // hay que descartarlo en silencio. Va después de `ejecutar` porque este
    // limpia el mensaje al arrancar.
    if (parcial) {
      accion.mostrarAviso(
        `Se autorizaron ${parcial.procesados}. ` +
          `${parcial.sin_aprobador!.length} quedaron sin autorizar porque no tienen Aprobador asignado — ` +
          `asignalo y volvé a intentar.`
      );
    }
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf" | "adjunto") {
    try {
      const url = await api.obtenerBlobSasUrl(id, tipo);
      abrirEnNuevaPestana(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  if (loading) return <p>Cargando bandeja...</p>;
  if (error) return <p className="error-text">Error: {error}</p>;

  const todasLaPaginaSeleccionadas =
    tabla.filas.length > 0 && tabla.filas.every((i) => selected.has(i.id));

  // Si alguna seleccionada no tiene Aprobador, el backend la rechazaría — mejor
  // avisarlo antes de mandar el lote que devolver un resultado parcial.
  const faltanAprobadores = items.some((i) => selected.has(i.id) && !aprobadorDe(i));

  return (
    <div>
      <h1>Facturas — Carril A</h1>
      <p className="subtitle">
        Paso 1 de 2: revisar cuenta contable y responsable, y autorizar antes de que pase al Aprobador.
      </p>

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={tabla.filtradas}
        totalSinFiltro={items.length}
      />

      <AvisoError mensaje={accion.error} onCerrar={accion.limpiarError} />

      {selected.size > 0 && (
        <div className="bulk-bar">
          <span>{selected.size} factura(s) seleccionada(s)</span>
          <button
            disabled={accion.enCurso || faltanAprobadores}
            title={
              faltanAprobadores
                ? "Alguna de las seleccionadas no tiene Aprobador asignado"
                : undefined
            }
            onClick={autorizarSeleccionadas}
          >
            {accion.enCurso ? "Procesando..." : ETIQUETA_AUTORIZAR_CONTADURIA_LOTE}
          </button>
        </div>
      )}

      <table className="compact">
        <colgroup>
          <col style={{ width: "3%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "17%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={todasLaPaginaSeleccionadas}
                title="Seleccionar todas las de esta pagina"
                onChange={(e) => toggleSelectedPagina(e.target.checked)}
              />
            </th>
            <ThOrdenable columna="nro" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Documento
            </ThOrdenable>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="monto" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Monto
            </ThOrdenable>
            <ThOrdenable columna="fecha_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Fecha doc.
            </ThOrdenable>
            <th>Cuenta contable</th>
            <th>Aprobador</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={COLUMNAS} className="empty-row">
                {items.length === 0
                  ? "No hay facturas pendientes."
                  : "Ninguna factura coincide con la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item) => {
            const cuenta = cuentaDe(item);
            // Bloquea autorizar si se tipeó una cuenta que no está en el
            // catálogo — sin esto se podría mandar a SAP una cuenta inexistente.
            const cuentaOk = item.requiere_reparto_manual || esCuentaValida(cuenta, cuentas);
            // Y bloquea si no hay Aprobador: sin él la factura no aparecería en
            // la bandeja de nadie (la del Aprobador filtra por asignación).
            const aprobadorOk = Boolean(aprobadorDe(item));
            return (
              <Fragment key={item.id}>
                <tr>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={(e) => toggleSelected(item.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <CfeIdentidad
                      tipoCfe={item.tipo_cfe}
                      serie={item.serie}
                      nro={item.nro}
                      rutEmisor={item.rut_emisor}
                    />
                  </td>
                  <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                    {item.proveedor_nombre}
                  </td>
                  <td className="mono celda-monto">
                    {formatMonto(item.monto)}
                    <span className="moneda">{item.moneda}</span>
                  </td>
                  <td className="celda-fecha">
                    <input
                      type="date"
                      defaultValue={item.fecha_documento}
                      title={`Fecha del documento: ${formatFecha(item.fecha_documento)}`}
                      onChange={(e) =>
                        setEdiciones((prev) => ({ ...prev, [item.id]: { ...prev[item.id], fecha: e.target.value } }))
                      }
                    />
                  </td>
                  <td>
                    {item.requiere_reparto_manual ? (
                      // Regla de negocio: reparto variable -> SIEMPRE solo lectura,
                      // nunca selector. Ver docstring del componente.
                      <span title="Reparto variable — se completa en SAP, no aca">
                        {item.cuenta_vigente} <em>(reparto en SAP)</em>
                      </span>
                    ) : (
                      <CuentaCombobox
                        valor={cuenta}
                        cuentas={cuentas}
                        onChange={(valor) =>
                          setEdiciones((prev) => ({ ...prev, [item.id]: { ...prev[item.id], cuenta: valor } }))
                        }
                      />
                    )}
                  </td>
                  <td>
                    <select
                      defaultValue={item.comprador_asignado ?? ""}
                      onChange={(e) =>
                        setEdiciones((prev) => ({ ...prev, [item.id]: { ...prev[item.id], comprador: e.target.value } }))
                      }
                    >
                      <option value="">— sin asignar —</option>
                      {compradores.map((c) => (
                        <option key={c.upn} value={c.upn}>
                          {c.nombre}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="actions-cell">
                    <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                      XML
                    </button>
                    {item.pdf_blob_url && (
                      <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                        PDF
                      </button>
                    )}
                    <LineasToggle expanded={expandidos.has(item.id)} onToggle={() => toggleExpandido(item.id)} />
                    {/* [NUEVO 08/09/2026] Comprobante adicional opcional. */}
                    <NotasToggle
                      expanded={notasAbiertas.has(item.id)}
                      tieneAdjunto={Boolean(item.adjunto_blob_url)}
                      onToggle={() => toggleNotas(item.id)}
                    />
                    {/* Deshabilitado mientras corre: la accion puede tardar
                        varios segundos y los POST no son idempotentes, asi que
                        no hay que invitar al doble clic. */}
                    <button
                      disabled={!cuentaOk || !aprobadorOk || accion.enCurso}
                      title={
                        !aprobadorOk
                          ? "Falta asignar un Aprobador — es obligatorio para autorizar"
                          : !cuentaOk
                            ? "La cuenta contable no existe en el catalogo"
                            : undefined
                      }
                      onClick={() => autorizar(item.id)}
                    >
                      {ETIQUETA_AUTORIZAR_CONTADURIA}
                    </button>
                    <button
                      className="btn-danger"
                      disabled={accion.enCurso}
                      onClick={() => setRechazando(item)}
                    >
                      Rechazar
                    </button>
                  </td>
                </tr>
                {expandidos.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={COLUMNAS} />}
                {notasAbiertas.has(item.id) && (
                  <NotasAdjuntoRow
                    item={item}
                    colSpan={COLUMNAS}
                    /* Sin comentario: el comentario libre es del Paso 2. */
                    puedeAdjuntar
                    onElegirArchivo={(file) => subirAdjunto(item.id, file)}
                    onVerAdjunto={() => abrirDocumento(item.id, "adjunto")}
                    enCurso={accion.enCurso}
                  />
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />

      <ModalMotivo
        abierto={rechazando !== null}
        titulo="Rechazar factura"
        detalle={
          rechazando
            ? `${rechazando.proveedor_nombre ?? rechazando.rut_emisor} — ${rechazando.serie}-${rechazando.nro}`
            : undefined
        }
        onConfirmar={confirmarRechazo}
        onCancelar={() => setRechazando(null)}
      />
    </div>
  );
}
